import { writable, derived, get } from "svelte/store";
import axios from "../../utiles/axios.util";
import { Preferences } from "@capacitor/preferences";
import {
    getDefault20WeekRange,
    getWeekFromDate,
    getWeekRangeInfo,
    getDateRangeFromWeeks,
    generateActiveWeeksAndMonths,
    parseDate,
    formatDate,
    getPresetDateRanges,
} from "./dateWeekUtil.js";

// 1년 전체 주차 정의 (1주차 ~ 53주차)
export const ALL_WEEKS = Array.from({ length: 53 }, (_, i) => {
    const week = i + 1;
    // 월 매핑 계산
    let month = 1;
    if (week >= 49) month = 12;
    else if (week >= 45) month = 11;
    else if (week >= 40) month = 10;
    else if (week >= 36) month = 9;
    else if (week >= 32) month = 8;
    else if (week >= 27) month = 7;
    else if (week >= 23) month = 6;
    else if (week >= 19) month = 5;
    else if (week >= 14) month = 4;
    else if (week >= 10) month = 3;
    else if (week >= 6) month = 2;
    else month = 1;

    return {
        week,
        month,
        label: `${week}주`,
        isActual: false,
    };
});

// 1월 ~ 12월 월별 주차 묶음 목록
export const MONTH_LIST = Array.from({ length: 12 }, (_, i) => {
    const m = i + 1;
    return {
        month: m,
        label: `${m}월`,
        weeks: ALL_WEEKS.filter(w => w.month === m).map(w => w.week)
    };
});

// DB에서 불러온 기준주차 / 비교주차 목록 Store (SELECT DISTINCT PLAN_WEEK_NO FROM BSN_MNGMT)
export const planWeeks = writable([]);

// DB에서 불러온 제품유형 목록 Store (SELECT DISTINCT MLSFC_CODE, MLSFC_NM FROM UVW_ITEM_GROUP)
export const productTypes = writable([]);

// DB에서 불러온 품목그룹 목록 Store (SELECT ITEM_GROUP_CODE, ITEM_GROUP_NM FROM UVW_ITEM_GROUP)
export const itemGroups = writable([]);

// DB에서 불러온 통합기종 목록 Store (SELECT REFRN_CODE, REFRN_NM FROM UVW_REFRN_CODE WHERE CL_CODE = 'XS017')
export const modelIntegrations = writable([]);

// DB에서 불러온 기종 목록 Store (SELECT DISTINCT MHRLS_KND_CODE FROM BSN_MNGMT)
export const models = writable([]);

// 기준주차/비교주차 API 조회
export async function fetchPlanWeeks() {
    try {
        const res = await axios.get("bsnMngmt/planWeeks");
        if (res && res.data && res.data.code === 200 && Array.isArray(res.data.data)) {
            const list = res.data.data.map(item => {
                const code = item.plan_week_no || item.PLAN_WEEK_NO;
                return {
                    code,
                    name: code,
                };
            });
            planWeeks.set(list);
            return list;
        }
    } catch (e) {
        console.error("fetchPlanWeeks error:", e);
    }
    return [];
}

// 제품유형 API 조회
export async function fetchProductTypes() {
    try {
        const res = await axios.get("bsnMngmt/productTypes");
        if (res && res.data && res.data.code === 200 && Array.isArray(res.data.data)) {
            const list = res.data.data.map(item => ({
                mlsfc_code: String(item.mlsfc_code || item.MLSFC_CODE || "").trim(),
                mlsfc_nm: String(item.mlsfc_nm || item.MLSFC_NM || "").trim(),
            })).filter(x => x.mlsfc_code);
            productTypes.set(list);
            return list;
        }
    } catch (e) {
        console.error("fetchProductTypes error:", e);
    }
    return [];
}

// 제품분류 (품목그룹) API 조회
export async function fetchItemGroups() {
    try {
        const res = await axios.get("bsnMngmt/itemGroups");
        if (res && res.data && res.data.code === 200 && Array.isArray(res.data.data)) {
            const list = res.data.data.map(item => ({
                mlsfc_code: String(item.mlsfc_code || item.MLSFC_CODE || "").trim(),
                item_group_code: String(item.item_group_code || item.ITEM_GROUP_CODE || "").trim(),
                item_group_nm: String(item.item_group_nm || item.ITEM_GROUP_NM || item.item_grup_nm || item.ITEM_GRUP_NM || "").trim(),
            })).filter(x => x.item_group_code);
            itemGroups.set(list);
            return list;
        }
    } catch (e) {
        console.error("fetchItemGroups error:", e);
    }
    return [];
}

// 제품 분류 트리 (제품유형 > 품목그룹 > 기종) 3단계 목록 Store (UVW_ITEM_GROUP_UNITY)
export const itemGroupTree = writable([]);

// 제품 분류 트리 (제품유형 > 품목그룹 > 기종) API 조회
export async function fetchItemGroupTree() {
    try {
        const res = await axios.get("bsnMngmt/itemGroupTree");
        if (res && res.data && res.data.code === 200 && Array.isArray(res.data.data)) {
            const list = res.data.data.map(item => ({
                mlsfc_code: String(item.mlsfc_code || item.MLSFC_CODE || "").trim(),
                mlsfc_nm: String(item.mlsfc_nm || item.MLSFC_NM || "").trim(),
                item_group_code: String(item.item_group_code || item.ITEM_GROUP_CODE || "").trim(),
                item_group_nm: String(item.item_group_nm || item.ITEM_GROUP_NM || "").trim(),
                mhrls_knd_code: String(item.mhrls_knd_code || item.MHRLS_KND_CODE || "").trim(),
            })).filter(x => x.item_group_code);
            itemGroupTree.set(list);
            return list;
        }
    } catch (e) {
        console.error("fetchItemGroupTree error:", e);
    }
    return [];
}

// 통합기종 API 조회
export async function fetchModelIntegrations() {
    try {
        const res = await axios.get("bsnMngmt/modelIntegrations");
        if (res && res.data && res.data.code === 200 && Array.isArray(res.data.data)) {
            const list = res.data.data.map(item => ({
                refrn_code: item.refrn_code || item.REFRN_CODE || item.mhrls_unity_code || item.MHRLS_UNITY_CODE,
                refrn_nm: item.refrn_nm || item.REFRN_NM || item.mhrls_unity_nm || item.MHRLS_UNITY_NM,
                mhrls_unity_code: item.mhrls_unity_code || item.MHRLS_UNITY_CODE || item.refrn_code || item.REFRN_CODE,
                mhrls_unity_nm: item.mhrls_unity_nm || item.MHRLS_UNITY_NM || item.refrn_nm || item.REFRN_NM,
            }));
            modelIntegrations.set(list);
            return list;
        }
    } catch (e) {
        console.error("fetchModelIntegrations error:", e);
    }
    return [];
}

// 기종 API 조회
export async function fetchModels() {
    try {
        const res = await axios.get("bsnMngmt/models");
        if (res && res.data && res.data.code === 200 && Array.isArray(res.data.data)) {
            const list = res.data.data.map(item => ({
                mhrls_knd_code: item.mhrls_knd_code || item.MHRLS_KND_CODE,
                mhrls_knd_nm: item.mhrls_knd_nm || item.MHRLS_KND_NM || item.mhrls_knd_code || item.MHRLS_KND_CODE,
                mhrls_unity_code: item.mhrls_unity_code || item.MHRLS_UNITY_CODE,
            }));
            models.set(list);
            return list;
        }
    } catch (e) {
        console.error("fetchModels error:", e);
    }
    return [];
}

// 검색 메타 데이터 통합 초기화 함수 (한번에 가져오기)
export async function initSupplyPlanMeta() {
    try {
        const res = await axios.get("bsnMngmt/initOptions");
        if (res && res.data && res.data.code === 200 && res.data.data) {
            const data = res.data.data;
            const pwList = (data.planWeeks || []).map(item => {
                const code = item.plan_week_no || item.PLAN_WEEK_NO;
                return { code, name: code };
            });
            const ptList = (data.productTypes || []).map(item => ({
                mlsfc_code: item.mlsfc_code || item.MLSFC_CODE,
                mlsfc_nm: item.mlsfc_nm || item.MLSFC_NM,
            }));
            const igList = (data.itemGroups || []).map(item => ({
                mlsfc_code: item.mlsfc_code || item.MLSFC_CODE,
                item_group_code: item.item_group_code || item.ITEM_GROUP_CODE,
                item_group_nm: item.item_group_nm || item.ITEM_GROUP_NM || item.item_grup_nm || item.ITEM_GRUP_NM,
            }));
            const miList = (data.modelIntegrations || []).map(item => ({
                refrn_code: item.refrn_code || item.REFRN_CODE || item.mhrls_unity_code || item.MHRLS_UNITY_CODE,
                refrn_nm: item.refrn_nm || item.REFRN_NM || item.mhrls_unity_nm || item.MHRLS_UNITY_NM,
                mhrls_unity_code: item.mhrls_unity_code || item.MHRLS_UNITY_CODE || item.refrn_code || item.REFRN_CODE,
                mhrls_unity_nm: item.mhrls_unity_nm || item.MHRLS_UNITY_NM || item.refrn_nm || item.REFRN_NM,
            }));
            const mList = (data.models || []).map(item => ({
                mhrls_knd_code: item.mhrls_knd_code || item.MHRLS_KND_CODE,
                mhrls_knd_nm: item.mhrls_knd_nm || item.MHRLS_KND_NM || item.mhrls_knd_code || item.MHRLS_KND_CODE,
                mhrls_unity_code: item.mhrls_unity_code || item.MHRLS_UNITY_CODE,
            }));

            planWeeks.set(pwList);
            productTypes.set(ptList);
            itemGroups.set(igList);
            modelIntegrations.set(miList);
            models.set(mList);

            supplyPlanSearch.update(s => {
                let newBase = s.baseWeek;
                let newCompare = s.compareWeek;
                if (pwList && pwList.length > 0) {
                    if (!newBase || !pwList.some(p => p.code === newBase)) {
                        newBase = pwList[0].code;
                        newCompare = pwList.length > 1 ? pwList[1].code : pwList[0].code;
                    }
                }
                const updated = {
                    ...s,
                    baseWeek: newBase,
                    compareWeek: newCompare,
                };
                appliedSupplyPlanSearch.set(JSON.parse(JSON.stringify(updated)));
                return updated;
            });

            return { pwList, ptList, igList, miList, mList };
        }
    } catch (e) {
        console.warn("initSupplyPlanMeta single API error, falling back to multi-fetch:", e);
    }

    // 폴백: 개별 호출
    const [pwList, ptList, igList, miList, mList] = await Promise.all([
        fetchPlanWeeks(),
        fetchProductTypes(),
        fetchItemGroups(),
        fetchModelIntegrations(),
        fetchModels()
    ]);
    return { pwList, ptList, igList, miList, mList };
}
// 현재 일자의 주차 기준 과거 1주 ~ 미래 19주 (총 21주 구간) 자동 계산값
export const autoWeekRange = getDefault20WeekRange();

// 페이지 열기/초기화 시 적용될 기본 조회구간 (사용자 지정 시 isCustom: true)
export const defaultWeekRange = writable({ ...autoWeekRange });

const currentYear = new Date().getFullYear();

// 검색 및 필터 상태 (입력 UI 전용)
export const supplyPlanSearch = writable({
    excludeZero: true,          // 0 제외
    isMultiWeek: false,         // 복수주차 비교 (기본값: 체크 해제)
    monthSum: false,            // 월 합계 모드 (주차 컬럼 숨기고 월/년 합계만 표시)
    startDate: autoWeekRange.startDate, // 시작일자 (YYYY-MM-DD)
    endDate: autoWeekRange.endDate,     // 종료일자 (YYYY-MM-DD)
    startYear: autoWeekRange.startYear, // 시작년도 (자동 도출)
    endYear: autoWeekRange.endYear,     // 종료년도 (자동 도출)
    startWeek: autoWeekRange.startWeek, // 시작주차 (자동 도출)
    endWeek: autoWeekRange.endWeek,     // 종료주차 (자동 도출)
    baseWeek: "",               // 기준 주차
    compareWeek: "",            // 비교 주차
    itemKeyword: "",            // 품목조회 (품목코드 또는 품명 함께 조회)
    productType: "",            // 제품유형 (MLSFC_CODE)
    itemGroup: "",              // 품목그룹 (ITEM_GROUP_CODE)
    modelIntegration: "",       // 통합기종 (MHRLS_UNITY_CODE)
    model: "",                  // 기종 (MHRLS_KND_CODE)
    selectedItemGroups: [],     // 다중 선택 품목그룹 목록 (트리 선택)
    selectedModels: [],         // 다중 선택 기종 목록 (트리 선택)
    selectedGroupModels: [],    // 트리에서 일부 기종만 선택된 품목그룹의 "품목그룹코드|기종코드" 목록
    selectedTreeIds: [],        // 트리 모달에서 선택된 고유 노드 ID 목록
    category: "ALL",            // 호환용
    itemCode: "",               // 호환용
    region: "ALL",              // 영업 권역
    team: "ALL",                // 영업 팀
    customer: "",               // 거래선
    isExpanded: true,           // 조회 조건 패널 접기/펼치기
});

// 실제로 조회에 적용된 상태 스토어 ('조회하기' 버튼 클릭 시에만 갱신)
export const appliedSupplyPlanSearch = writable({
    excludeZero: true,
    isMultiWeek: false,
    monthSum: false,
    startDate: autoWeekRange.startDate,
    endDate: autoWeekRange.endDate,
    startYear: autoWeekRange.startYear,
    endYear: autoWeekRange.endYear,
    startWeek: autoWeekRange.startWeek,
    endWeek: autoWeekRange.endWeek,
    baseWeek: "",
    compareWeek: "",
    itemKeyword: "",
    productType: "",
    itemGroup: "",
    modelIntegration: "",
    model: "",
    selectedItemGroups: [],
    selectedModels: [],
    selectedGroupModels: [],
    selectedTreeIds: [],
    category: "ALL",
    itemCode: "",
    region: "ALL",
    team: "ALL",
    customer: "",
    isExpanded: true,
});

// 전체 지원 제품 컬럼 정의
export const MASTER_COLUMNS_DEF = [
    { key: "productType", label: "제품유형", group: "제품", desc: "환경장비, 제설장비 등" },
    { key: "itemGroup", label: "품목그룹", group: "제품", desc: "살포기, 살수차, 노면차, 제설기 등" },
    { key: "integratedModel", label: "통합기종", group: "제품", desc: "대형, 중형, 소형" },
    { key: "model", label: "기종", group: "제품", desc: "1톤, 8.5톤, 15톤_N, 16톤 등" },
    { key: "itemCode", label: "품번", group: "제품", desc: "고유 부품/완제품 코드" },
    { key: "itemName", label: "품명", group: "제품", desc: "완제품 모델명" },
];

// 사용자가 설정한 표시 컬럼 목록 (순서 반영: 품목그룹, 품번, 기종)
export const visibleMasterColumns = writable([
    "itemGroup",
    "itemCode",
    "model"
]);

// 6대 PSI 핵심 항목 정의 및 상세 설명 (DB IEM_CODE: R, S, D, P, I 및 차질 공식 반영)
export const METRIC_DEFINITIONS = [
    {
        key: "order",
        label: "수주",
        badge: "R",
        summary: "수주 수량 (IEM_CODE: R)",
        descPast: "과거) 수주 실적",
        descFuture: "미래) 수주 계획"
    },
    {
        key: "sales",
        label: "판매",
        badge: "S",
        summary: "판매 수량 (IEM_CODE: S)",
        descPast: "과거) 판매 실적",
        descFuture: "미래) 판매 계획"
    },
    {
        key: "shortage",
        label: "차질",
        badge: "차질",
        summary: "차질 = 수주 - 출하 (0보다 클 때만 표시, 마이너스는 0)",
        descPast: "수주 대비 출하 차질",
        descFuture: "수주 대비 출하 차질"
    },
    {
        key: "shipment",
        label: "출하",
        badge: "D",
        summary: "출하 수량 (IEM_CODE: D)",
        descPast: "과거) 출하 실적",
        descFuture: "미래) 출하 계획"
    },
    {
        key: "production",
        label: "생산",
        badge: "P",
        summary: "생산 수량 (IEM_CODE: P)",
        descPast: "과거) 생산 실적",
        descFuture: "미래) 생산 계획"
    },
    {
        key: "stock",
        label: "재고",
        badge: "I",
        summary: "재고 수량 (IEM_CODE: I)",
        descPast: "과거) 기말 재고 실적",
        descFuture: "미래) 기말 재고 계획"
    }
];

// 계획주차 오른쪽에 표시할 항목 키 목록 (기본: 6가지 모두 표시)
export const ALL_METRIC_KEYS = METRIC_DEFINITIONS.map(m => m.key);
export const visibleMetricKeys = writable([...ALL_METRIC_KEYS]);

// 제품 컬럼 기본 구성
export const DEFAULT_VISIBLE_COLUMNS = ["itemGroup", "itemCode", "model"];

// ──────────────────────────────────────────────────────────────
// 컬럼 설정 영속화 (표시 컬럼 / 표시 항목 / 기본 조회구간)
// - 서버 DB가 아닌 기기 로컬 저장소(Capacitor Preferences = 웹에서는 localStorage)에 저장
// - 로그아웃 시에는 로그인/토큰 키만 삭제하므로 로그아웃 후 재로그인해도 유지됨
// - 설정은 기기(브라우저)별로 저장되며, 다른 PC에서는 공유되지 않음
// ──────────────────────────────────────────────────────────────
const SETTINGS_PREF_KEY = "supplyPlanColumnSettings";
function isValidDateRange(range) {
    if (!range || !range.startDate || !range.endDate) return false;
    const s = parseDate(range.startDate);
    const e = parseDate(range.endDate);
    return Boolean(s && e && s <= e);
}

// 기본 조회구간 설정 (range: { startWeek, endWeek } 또는 null = 현재주차 기준 자동)
// - 직접 지정된 주차 구간(예: 1~53주)은 매년 실행 시점의 '현재 연도'를 기준으로 날짜를 자동 계산합니다.
export function setDefaultWeekRange(range) {
    if (range) {
        const currentYear = new Date().getFullYear();
        let startW = range.startWeek != null ? parseInt(range.startWeek, 10) : null;
        let endW = range.endWeek != null ? parseInt(range.endWeek, 10) : null;

        // 기존 저장된 설정이 날짜(startDate, endDate) 형태인 경우 주차 추출 호환
        if ((startW == null || endW == null) && range.startDate && range.endDate) {
            const sw = getWeekFromDate(range.startDate);
            const ew = getWeekFromDate(range.endDate);
            if (sw && ew) {
                startW = sw.week;
                endW = ew.week;
            }
        }

        if (startW != null && endW != null && startW <= endW) {
            const { startDate, endDate } = getDateRangeFromWeeks(currentYear, startW, endW);
            const info = getWeekRangeInfo(startDate, endDate);
            if (info) {
                defaultWeekRange.set({
                    ...info,
                    startWeek: startW,
                    endWeek: endW,
                    isCustom: true,
                });
                return;
            }
        }
    }
    defaultWeekRange.set({ ...autoWeekRange, isCustom: false });
}

let settingsLoaded = false;

// 저장된 컬럼 설정을 불러와 스토어에 반영 (페이지 로드 시 1회만 수행)
export async function loadSupplyPlanSettings() {
    if (settingsLoaded) return;
    settingsLoaded = true;
    try {
        const { value } = await Preferences.get({ key: SETTINGS_PREF_KEY });
        if (!value) return;
        const saved = JSON.parse(value);

        if (Array.isArray(saved.columns)) {
            const cols = saved.columns.filter(k => MASTER_COLUMNS_DEF.some(c => c.key === k));
            if (cols.length > 0) visibleMasterColumns.set(cols);
        }
        if (Array.isArray(saved.metrics)) {
            const keys = ALL_METRIC_KEYS.filter(k => saved.metrics.includes(k));
            if (keys.length > 0) visibleMetricKeys.set(keys);
        }
        if (saved.weekRange) {
            setDefaultWeekRange(saved.weekRange);
            const { startDate, endDate, startYear, endYear, startWeek, endWeek } = get(defaultWeekRange);
            const applyRange = s => s.monthSum ? s : { ...s, startDate, endDate, startYear, endYear, startWeek, endWeek };
            supplyPlanSearch.update(applyRange);
            appliedSupplyPlanSearch.update(applyRange);
        }
    } catch (e) {
        console.warn("공급계획 컬럼 설정 불러오기 오류:", e);
    }
}

// 컬럼 설정 저장 (weekRange가 null이면 현재주차 기준 자동)
export async function saveSupplyPlanSettings({ columns, metrics, weekRange }) {
    try {
        await Preferences.set({
            key: SETTINGS_PREF_KEY,
            value: JSON.stringify({
                columns,
                metrics,
                weekRange: weekRange && weekRange.startWeek != null && weekRange.endWeek != null
                    ? {
                        startWeek: parseInt(weekRange.startWeek, 10),
                        endWeek: parseInt(weekRange.endWeek, 10)
                    }
                    : null,
            }),
        });
        return true;
    } catch (e) {
        console.error("공급계획 컬럼 설정 저장 오류:", e);
        return false;
    }
}

// 샘플 마스터 데이터 정의 (Image 3 구조 및 실제 리텍 제품군 반영)
export const RAW_PRODUCTS = [
    {
        id: "P1",
        team: "국내",
        region: "수도권",
        customer: "국토부",
        productType: "환경장비",
        itemGroup: "살포기",
        integratedModel: "중형",
        model: "8.5톤",
        itemCode: "9000000100",
        itemName: "다목적 살포기 8.5T",
        initialStock: 8,
        plans: {
            "202512_2": {
                orders:     { 12: 12, 13: 15, 14: 10, 15: 8, 16: 12, 17: 20, 18: 14, 19: 10, 20: 12, 21: 8, 22: 16, 23: 10, 24: 12, 25: 14, 26: 8, 27: 10, 28: 12, 29: 8, 30: 10, 31: 6 },
                moOrders:   { 12: 10, 13: 10, 14: 6,  15: 8, 16: 10, 17: 12, 18: 10, 19: 8,  20: 8,  21: 6, 22: 10, 23: 8,  24: 8,  25: 10, 26: 6, 27: 8,  28: 8,  29: 6, 30: 6, 31: 4 },
            },
            "202511_2": {
                orders:     { 12: 10, 13: 12, 14: 12, 15: 10, 16: 10, 17: 16, 18: 12, 19: 8,  20: 10, 21: 8, 22: 12, 23: 8,  24: 10, 25: 12, 26: 8, 27: 8,  28: 10, 29: 6, 30: 8, 31: 6 },
                moOrders:   { 12: 10, 13: 8,  14: 8,  15: 6, 16: 8,  17: 10, 18: 8,  19: 6,  20: 6,  21: 6, 22: 8,  23: 6,  24: 6,  25: 8,  26: 6, 27: 6,  28: 6,  29: 6, 30: 4, 31: 4 },
            }
        }
    },
    {
        id: "P2",
        team: "국내",
        region: "수도권",
        customer: "도공",
        productType: "환경장비",
        itemGroup: "살수차",
        integratedModel: "대형",
        model: "16톤",
        itemCode: "9000000110",
        itemName: "고압 살수차 16T",
        initialStock: 5,
        plans: {
            "202512_2": {
                orders:     { 12: 8, 13: 10, 14: 6, 15: 8, 16: 10, 17: 12, 18: 8, 19: 6, 20: 8, 21: 6, 22: 10, 23: 6, 24: 8, 25: 8, 26: 6, 27: 6, 28: 8, 29: 6, 30: 6, 31: 4 },
                moOrders:   { 12: 8, 13: 6,  14: 4, 15: 6, 16: 6,  17: 8,  18: 6, 19: 6, 20: 4, 21: 4, 22: 6,  23: 4, 24: 4, 25: 6, 26: 4, 27: 4, 28: 4, 29: 4, 30: 2, 31: 2 },
            },
            "202511_2": {
                orders:     { 12: 6, 13: 8,  14: 6, 15: 6, 16: 8,  17: 10, 18: 8, 19: 6, 20: 6, 21: 6, 22: 8,  23: 6, 24: 6, 25: 6, 26: 4, 27: 6, 28: 6, 29: 4, 30: 4, 31: 4 },
                moOrders:   { 12: 6, 13: 4,  14: 4, 15: 4, 16: 6,  17: 6,  18: 4, 19: 4, 20: 4, 21: 4, 22: 4,  23: 4, 24: 4, 25: 4, 26: 2, 27: 2, 28: 4, 29: 2, 30: 2, 31: 2 },
            }
        }
    },
    {
        id: "P3",
        team: "국내",
        region: "중부",
        customer: "국토부",
        productType: "환경장비",
        itemGroup: "노면차",
        integratedModel: "중형",
        model: "8.5톤",
        itemCode: "9000000120",
        itemName: "진공 노면청소차 8.5T",
        initialStock: 6,
        plans: {
            "202512_2": {
                orders:     { 12: 5, 13: 6, 14: 5, 15: 4, 16: 6, 17: 8, 18: 5, 19: 4, 20: 5, 21: 4, 22: 6, 23: 4, 24: 5, 25: 5, 26: 4, 27: 4, 28: 5, 29: 4, 30: 4, 31: 3 },
                moOrders:   { 12: 5, 13: 4, 14: 3, 15: 3, 16: 3, 17: 4, 18: 3, 19: 3, 20: 2, 21: 2, 22: 3, 23: 3, 24: 2, 25: 2, 26: 2, 27: 2, 28: 2, 29: 2, 30: 1, 31: 1 },
            },
            "202511_2": {
                orders:     { 12: 4, 13: 5, 14: 4, 15: 4, 16: 5, 17: 6, 18: 4, 19: 4, 20: 4, 21: 4, 22: 5, 23: 4, 24: 4, 25: 4, 26: 3, 27: 4, 28: 4, 29: 3, 30: 3, 31: 3 },
                moOrders:   { 12: 4, 13: 3, 14: 2, 15: 2, 16: 3, 17: 3, 18: 2, 19: 2, 20: 2, 21: 2, 22: 2, 23: 2, 24: 2, 25: 2, 26: 1, 27: 1, 28: 2, 29: 1, 30: 1, 31: 1 },
            }
        }
    },
    {
        id: "P4",
        team: "국내",
        region: "강원",
        customer: "도공",
        productType: "제설장비",
        itemGroup: "제설기",
        integratedModel: "대형",
        model: "15톤_N",
        itemCode: "9000000200",
        itemName: "고성능 전방 제설기 15T_N",
        initialStock: 10,
        plans: {
            "202512_2": {
                orders:     { 12: 2, 13: 3, 14: 2, 15: 2, 16: 3, 17: 4, 18: 3, 19: 2, 20: 3, 21: 2, 22: 4, 23: 3, 24: 4, 25: 5, 26: 8, 27: 12, 28: 15, 29: 18, 30: 20, 31: 22 },
                moOrders:   { 12: 2, 13: 1, 14: 1, 15: 1, 16: 2, 17: 2, 18: 2, 19: 2, 20: 2, 21: 2, 22: 2, 23: 2, 24: 3, 25: 4, 26: 5, 27: 7,  28: 8,  29: 10, 30: 12, 31: 14 },
            },
            "202511_2": {
                orders:     { 12: 2, 13: 2, 14: 2, 15: 2, 16: 2, 17: 3, 18: 2, 19: 2, 20: 2, 21: 2, 22: 3, 23: 3, 24: 4, 25: 5, 26: 7, 27: 10, 28: 14, 29: 16, 30: 18, 31: 20 },
                moOrders:   { 12: 2, 13: 1, 14: 1, 15: 1, 16: 1, 17: 2, 18: 1, 19: 1, 20: 1, 21: 1, 22: 1, 23: 1, 24: 2, 25: 3, 26: 4, 27: 5,  28: 6,  29: 8,  30: 10, 31: 12 },
            }
        }
    },
    {
        id: "P5",
        team: "국내",
        region: "영남",
        customer: "민수",
        productType: "환경장비",
        itemGroup: "분진차",
        integratedModel: "대형",
        model: "16톤",
        itemCode: "9000000130",
        itemName: "특수 분진흡입차 16T",
        initialStock: 3,
        plans: {
            "202512_2": {
                orders:     { 12: 3, 13: 4, 14: 3, 15: 2, 16: 4, 17: 5, 18: 3, 19: 2, 20: 3, 21: 2, 22: 4, 23: 2, 24: 3, 25: 3, 26: 2, 27: 2, 28: 3, 29: 2, 30: 2, 31: 2 },
                moOrders:   { 12: 3, 13: 2, 14: 1, 15: 1, 16: 2, 17: 2, 18: 2, 19: 2, 20: 1, 21: 1, 22: 2, 23: 1, 24: 2, 25: 2, 26: 1, 27: 1, 28: 2, 29: 1, 30: 1, 31: 1 },
            },
            "202511_2": {
                orders:     { 12: 2, 13: 3, 14: 2, 15: 2, 16: 3, 17: 4, 18: 2, 19: 2, 20: 2, 21: 2, 22: 3, 23: 2, 24: 2, 25: 2, 26: 2, 27: 2, 28: 2, 29: 2, 30: 2, 31: 1 },
                moOrders:   { 12: 2, 13: 1, 14: 1, 15: 1, 16: 1, 17: 2, 18: 1, 19: 1, 20: 1, 21: 1, 22: 1, 23: 1, 24: 1, 25: 1, 26: 1, 27: 1, 28: 1, 29: 1, 30: 1, 31: 1 },
            }
        }
    },
    {
        id: "P6",
        team: "국내",
        region: "호남",
        customer: "지자체",
        productType: "제설장비",
        itemGroup: "살포기",
        integratedModel: "소형",
        model: "1톤",
        itemCode: "9000000205",
        itemName: "소형 제설살포기 1T",
        initialStock: 4,
        plans: {
            "202512_2": {
                orders:     { 12: 2, 13: 2, 14: 1, 15: 2, 16: 3, 17: 3, 18: 2, 19: 1, 20: 2, 21: 1, 22: 2, 23: 2, 24: 2, 25: 3, 26: 4, 27: 6, 28: 8, 29: 10, 30: 12, 31: 14 },
                moOrders:   { 12: 2, 13: 1, 14: 1, 15: 1, 16: 1, 17: 1, 18: 1, 19: 1, 20: 1, 21: 1, 22: 1, 23: 1, 24: 1, 25: 2, 26: 2, 27: 3, 28: 4, 29: 5,  30: 6,  31: 7 },
            },
            "202511_2": {
                orders:     { 12: 1, 13: 2, 14: 1, 15: 1, 16: 2, 17: 2, 18: 1, 19: 1, 20: 1, 21: 1, 22: 2, 23: 1, 24: 2, 25: 2, 26: 3, 27: 5, 28: 6, 29: 8,  30: 10, 31: 12 },
                moOrders:   { 12: 1, 13: 1, 14: 1, 15: 1, 16: 1, 17: 1, 18: 1, 19: 1, 20: 1, 21: 1, 22: 1, 23: 1, 24: 1, 25: 1, 26: 1, 27: 2, 28: 3, 29: 4,  30: 5,  31: 6 },
            }
        }
    }
];

// PSI 시뮬레이션 계산 함수 (Image 2 & Image 4 공식 명세 100% 반영)
// 1. [생산계획 생성]: 제조오더가 부족할 때 부족분(Delta < 0)만 생산제안 수량으로 생성
// 2. [출하계획 생성]: 수주계획 주차/수량 기준. 단, 재고+생산계획 수량을 넘지 않는 범위
// 3. [차질]: 수주 - 출하 (부족치량 발생 시 경고)
// 4. [판매]: 세금계산서 발행 소요일수(14일 = 2주) 반영 주차 집계
// 5. [재고]: 전주말 재고 + 당주 생산 - 당주 출하. (월소계는 해당 월 마지막 주차의 기말 재고)
// PSI 시뮬레이션 계산 함수 (Image 2 & Image 4 공식 명세 100% 반영)
// 1. [생산계획 생성]: 제조오더가 부족할 때 부족분(Delta < 0)만 생산제안 수량으로 생성
// 2. [출하계획 생성]: 수주계획 주차/수량 기준. 단, 재고+생산계획 수량을 넘지 않는 범위
// 3. [차질]: 수주 - 출하 (부족치량 발생 시 경고)
// 4. [판매]: 세금계산서 발행 소요일수(14일 = 2주) 반영 주차 집계
// 5. [재고]: 전주말 재고 + 당주 생산 - 당주 출하. (월소계는 해당 월 마지막 주차의 기말 재고)
function calculatePSIMetrics(product, planWeekCode, activeMonths = null, activeWeeks = null) {
    const weeksData = {};
    const monthsData = {};

    const targetWeeks = (activeWeeks && activeWeeks.length > 0)
        ? activeWeeks
        : ALL_WEEKS.map(w => ({ key: String(w.week), year: null, week: w.week }));

    const targetMonths = (activeMonths && activeMonths.length > 0)
        ? activeMonths
        : MONTH_LIST.map(m => ({ key: String(m.month), month: m.month, weeks: m.weeks.map(w => ({ key: String(w), week: w })) }));

    targetWeeks.forEach(wItem => {
        const k = wItem.key;
        const yr = wItem.year;
        const w = wItem.week;

        // 연도별 플랜(product.yearlyPlans[yr]) 우선 탐색, 없으면 기존 product.plans 사용
        let plan = null;
        if (yr && product.yearlyPlans && product.yearlyPlans[yr]) {
            const yrPlans = product.yearlyPlans[yr];
            plan = yrPlans[planWeekCode] || yrPlans["ALL"] || (Object.values(yrPlans)[0] || {});
        } else if (product && product.plans) {
            plan = product.plans[planWeekCode] || product.plans["ALL"] || (Object.values(product.plans)[0] || {});
        }
        if (!plan) plan = {};

        const orders = plan.orders || {};
        const sales = plan.sales || {};
        const shipments = plan.shipments || {};
        const productions = plan.productions || plan.moOrders || {};
        const stocks = plan.stocks || {};

        const order = orders[w] || 0;
        const sale = sales[w] || 0;
        const shipment = shipments[w] || 0;
        const production = productions[w] || 0;
        const stock = stocks[w] || 0;

        const diff = order - shipment;
        const shortage = diff > 0 ? diff : 0;

        weeksData[k] = {
            order,
            sales: sale,
            shortage,
            shipment,
            production,
            stock,
        };
    });

    // 월별 집계
    targetMonths.forEach(mObj => {
        const mKey = mObj.key;
        let mOrder = 0;
        let mSales = 0;
        let mShortage = 0;
        let mShipment = 0;
        let mProd = 0;

        const mWeeks = mObj.weeks || [];
        mWeeks.forEach(wItem => {
            const wKey = typeof wItem === "object" ? wItem.key : String(wItem);
            const d = weeksData[wKey];
            if (d) {
                mOrder += d.order;
                mSales += d.sales;
                mShortage += d.shortage;
                mShipment += d.shipment;
                mProd += d.production;
            }
        });

        // 재고: 마지막 주차 기말 재고
        let mStock = 0;
        if (mWeeks.length > 0) {
            const lastW = mWeeks[mWeeks.length - 1];
            const lastWKey = typeof lastW === "object" ? lastW.key : String(lastW);
            mStock = weeksData[lastWKey] ? weeksData[lastWKey].stock : 0;
        }

        monthsData[mKey] = {
            order: mOrder,
            sales: mSales,
            shortage: mShortage,
            shipment: mShipment,
            production: mProd,
            stock: mStock,
        };
    });

    // 전체 계(Total) 계산
    let totalOrder = 0;
    let totalSales = 0;
    let totalShortage = 0;
    let totalShipment = 0;
    let totalProd = 0;

    targetWeeks.forEach(wItem => {
        const d = weeksData[wItem.key];
        if (d) {
            totalOrder += d.order;
            totalSales += d.sales;
            totalShortage += d.shortage;
            totalShipment += d.shipment;
            totalProd += d.production;
        }
    });

    let totalStock = 0;
    if (targetMonths.length > 0) {
        const lastM = targetMonths[targetMonths.length - 1];
        totalStock = monthsData[lastM.key] ? monthsData[lastM.key].stock : 0;
    }

    const totalData = {
        order: totalOrder,
        sales: totalSales,
        shortage: totalShortage,
        shipment: totalShipment,
        production: totalProd,
        stock: totalStock,
    };

    return { weeksData, monthsData, totalData };
}

// 두 편성 주차 간의 차이(Diff) 계산: 기준(Base) - 비교(Compare)
function calculateDiffMetrics(baseMetrics, compareMetrics, activeMonths = null, activeWeeks = null) {
    const weeksData = {};
    const monthsData = {};

    const targetWeeks = (activeWeeks && activeWeeks.length > 0)
        ? activeWeeks
        : ALL_WEEKS.map(w => ({ key: String(w.week) }));

    const targetMonths = (activeMonths && activeMonths.length > 0)
        ? activeMonths
        : MONTH_LIST.map(m => ({ key: String(m.month) }));

    targetWeeks.forEach(wItem => {
        const k = wItem.key;
        const b = baseMetrics.weeksData[k];
        const c = compareMetrics.weeksData[k];
        weeksData[k] = {
            order: (b ? b.order : 0) - (c ? c.order : 0),
            sales: (b ? b.sales : 0) - (c ? c.sales : 0),
            shortage: (b ? b.shortage : 0) - (c ? c.shortage : 0),
            shipment: (b ? b.shipment : 0) - (c ? c.shipment : 0),
            production: (b ? b.production : 0) - (c ? c.production : 0),
            stock: (b ? b.stock : 0) - (c ? c.stock : 0),
        };
    });

    targetMonths.forEach(mObj => {
        const k = mObj.key;
        const b = baseMetrics.monthsData[k];
        const c = compareMetrics.monthsData[k];
        monthsData[k] = {
            order: (b ? b.order : 0) - (c ? c.order : 0),
            sales: (b ? b.sales : 0) - (c ? c.sales : 0),
            shortage: (b ? b.shortage : 0) - (c ? c.shortage : 0),
            shipment: (b ? b.shipment : 0) - (c ? c.shipment : 0),
            production: (b ? b.production : 0) - (c ? c.production : 0),
            stock: (b ? b.stock : 0) - (c ? c.stock : 0),
        };
    });

    const bTot = baseMetrics.totalData;
    const cTot = compareMetrics.totalData;
    const totalData = {
        order: (bTot ? bTot.order : 0) - (cTot ? cTot.order : 0),
        sales: (bTot ? bTot.sales : 0) - (cTot ? cTot.sales : 0),
        shortage: (bTot ? bTot.shortage : 0) - (cTot ? cTot.shortage : 0),
        shipment: (bTot ? bTot.shipment : 0) - (cTot ? cTot.shipment : 0),
        production: (bTot ? bTot.production : 0) - (cTot ? cTot.production : 0),
        stock: (bTot ? bTot.stock : 0) - (cTot ? cTot.stock : 0),
    };

    return { weeksData, monthsData, totalData };
}

// 품번/품목 검색 패턴 파서 (^ 및 % 지원)
function matchPattern(value, pattern) {
    if (!pattern || pattern.trim() === "") return true;
    const targets = pattern.split("^").map(p => p.trim()).filter(Boolean);
    const val = String(value || "").toLowerCase();

    return targets.some(target => {
        if (target.includes("%")) {
            const regexStr = target.replace(/%/g, ".*").toLowerCase();
            const regex = new RegExp(`^${regexStr}$`, "i");
            return regex.test(val);
        }
        return val.includes(target.toLowerCase());
    });
}

// DB에서 가져온 원본 데이터를 보관하는 Store (기본값: 기획 검증용 샘플 데이터)
export const rawProductsStore = writable(RAW_PRODUCTS);

// 데이터 조회 상태 관리 (API 호출 중 여부, 실데이터 vs 더미데이터 출처)
export const isDataLoading = writable(false);
export const dataStatus = writable({
    isFromApi: false,
    message: "더미 데이터 표시 중",
    count: RAW_PRODUCTS.length
});

/**
 * BSN_MNGMT 정규화 행 데이터(List<BsnMngmtDto>)를 프론트엔드 PSI 제품 그리드 구조로 피벗 변환
 */
export function transformBsnMngmtRowsToProducts(rows, defaultBaseWeek = "", defaultCompareWeek = "") {
    if (!rows || rows.length === 0) return [];
    const productMap = {};

    function createEmptyPlan() {
        return {
            orders: {},      // R: 수주
            sales: {},       // S: 판매
            shipments: {},   // D: 출하
            productions: {}, // P: 생산
            stocks: {},      // I: 재고
            moOrders: {},
            rawMetrics: {}
        };
    }

    const METRIC_FIELDS = ["orders", "sales", "shipments", "productions", "stocks", "moOrders"];

    // 1단계: 행 순회하며 제품 메타데이터 및 연도별/플랜별 데이터 버킷에 수집
    rows.forEach(r => {
        const itemCode = String(r.item_code || r.ITEM_CODE || "UNKNOWN").trim();
        const groupNm = String(r.item_group_nm || r.ITEM_GROUP_NM || r.item_group_code || r.ITEM_GROUP_CODE || "기타").trim();
        const groupCode = String(r.item_group_code || r.ITEM_GROUP_CODE || "").trim();
        const mlsfcCode = String(r.mlsfc_code || r.MLSFC_CODE || "").trim();
        const mlsfcNm = String(r.mlsfc_nm || r.MLSFC_NM || r.item_div_nm || r.ITEM_DIV_NM || "").trim();
        const integratedModelNm = String(r.mhrls_unity_nm || r.MHRLS_UNITY_NM || r.mhrls_unity_code || r.MHRLS_UNITY_CODE || "").trim();
        const integratedModelCode = String(r.mhrls_unity_code || r.MHRLS_UNITY_CODE || "").trim();
        const modelKindCode = String(r.mhrls_knd_code || r.MHRLS_KND_CODE || "").trim();
        const modelNm = modelKindCode || String(r.stndrd || r.STNDRD || integratedModelNm || itemCode).trim();
        const itemNm = String(r.item_nm || r.ITEM_NM || r.rm || r.RM || ("제품 " + itemCode)).trim();
        const itemSpec = String(r.stndrd || r.STNDRD || "").trim();

        if (!productMap[itemCode]) {
            productMap[itemCode] = {
                id: itemCode,
                team: "국내",
                region: "수도권",
                customer: "일반",
                productType: mlsfcNm || mlsfcCode || "기타",
                productTypeCode: mlsfcCode,
                itemGroup: groupNm,
                itemGroupCode: groupCode,
                integratedModel: integratedModelNm,
                integratedModelCode: integratedModelCode,
                model: modelNm,
                modelCode: modelKindCode,
                itemCode: itemCode,
                itemName: itemNm,
                itemSpec,       // 규격 (BSS_ITEM.STNDRD) - 품번 셀 툴팁 표시용
                initialStock: 0,
                plans: {},
                yearlyData: {}, // { [year]: { commonActuals: plan, plans: { [planCode]: plan } } }
            };
        }

        const prod = productMap[itemCode];
        const year = String(r.year || r.YEAR || "UNKNOWN").trim();
        if (!prod.yearlyData[year]) {
            prod.yearlyData[year] = {
                commonActuals: createEmptyPlan(),
                plans: {}
            };
        }

        const yData = prod.yearlyData[year];
        const rawPlanCode = String(r.plan_week_no || r.PLAN_WEEK_NO || "").trim();
        const week = parseInt(r.week_no || r.WEEK_NO, 10);
        const val = Number(r.week_value != null ? r.week_value : r.WEEK_VALUE) || 0;
        const iem = String(r.iem_code || r.IEM_CODE || "").trim().toUpperCase();

        const targetBucket = !rawPlanCode
            ? yData.commonActuals
            : (yData.plans[rawPlanCode] || (yData.plans[rawPlanCode] = createEmptyPlan()));

        if (iem === "R" || iem === "ORDER" || iem === "수주") {
            targetBucket.orders[week] = val;
        } else if (iem === "S" || iem === "SALES" || iem === "판매") {
            targetBucket.sales[week] = val;
        } else if (iem === "D" || iem === "DN" || iem === "SHIPMENT" || iem === "출하") {
            targetBucket.shipments[week] = val;
        } else if (iem === "P" || iem === "PD" || iem === "MO" || iem === "생산") {
            targetBucket.productions[week] = val;
            targetBucket.moOrders[week] = val;
        } else if (iem === "I" || iem === "STOCK" || iem === "재고") {
            targetBucket.stocks[week] = val;
        }
        if (!targetBucket.rawMetrics[iem]) targetBucket.rawMetrics[iem] = {};
        targetBucket.rawMetrics[iem][week] = val;
    });

    // 대상 플랜 키 목록 (기준주차, 비교주차)
    const targetPlanKeys = new Set();
    if (defaultBaseWeek && defaultBaseWeek !== "ALL") targetPlanKeys.add(defaultBaseWeek);
    if (defaultCompareWeek && defaultCompareWeek !== "ALL") targetPlanKeys.add(defaultCompareWeek);

    // 2단계: 연도별 데이터 확정 및 다년도 누적 합산 처리
    Object.values(productMap).forEach(prod => {
        const years = Object.keys(prod.yearlyData).sort(); // 오름차순 (예: ["2025", "2026"])

        // 각 연도별로 유효 플랜 데이터 생성: yearlyResolvedPlans[year] = { [planKey]: resolvedPlan }
        const yearlyResolvedPlans = {};

        years.forEach(yr => {
            const yData = prod.yearlyData[yr];
            yearlyResolvedPlans[yr] = {};

            const yrPlanKeys = new Set(Object.keys(yData.plans));
            targetPlanKeys.forEach(pk => yrPlanKeys.add(pk));
            yrPlanKeys.add("ALL");

            yrPlanKeys.forEach(pk => {
                const basePlan = yData.plans[pk] || createEmptyPlan();
                const resolved = createEmptyPlan();

                // 해당 플랜에 이미 있는 값 복사
                METRIC_FIELDS.forEach(field => {
                    Object.entries(basePlan[field] || {}).forEach(([wStr, val]) => {
                        resolved[field][parseInt(wStr, 10)] = val;
                    });
                });

                // 미등록 주차에 해당 연도의 실적(commonActuals) 보충
                METRIC_FIELDS.forEach(field => {
                    const caMap = yData.commonActuals[field] || {};
                    Object.entries(caMap).forEach(([wStr, val]) => {
                        const w = parseInt(wStr, 10);
                        if (resolved[field][w] === undefined) {
                            resolved[field][w] = val;
                        }
                    });
                });

                // "ALL" 플랜인 경우, 기준주차(또는 해당 연도의 첫 플랜)의 미래 데이터도 보충
                if (pk === "ALL") {
                    const firstPlanKey = (defaultBaseWeek && yData.plans[defaultBaseWeek])
                        ? defaultBaseWeek
                        : Object.keys(yData.plans)[0];
                    if (firstPlanKey && yData.plans[firstPlanKey]) {
                        const fp = yData.plans[firstPlanKey];
                        METRIC_FIELDS.forEach(field => {
                            Object.entries(fp[field] || {}).forEach(([wStr, val]) => {
                                const w = parseInt(wStr, 10);
                                if (resolved[field][w] === undefined) {
                                    resolved[field][w] = val;
                                }
                            });
                        });
                    }
                }

                yearlyResolvedPlans[yr][pk] = resolved;
            });
        });

        // 3단계: 최종 prod.plans 버킷 생성 및 연도별 데이터 합산 (다년도 누적)
        const allTargetKeys = new Set(targetPlanKeys);
        allTargetKeys.add("ALL");
        years.forEach(yr => {
            Object.keys(yearlyResolvedPlans[yr] || {}).forEach(k => allTargetKeys.add(k));
        });

        allTargetKeys.forEach(pk => {
            prod.plans[pk] = createEmptyPlan();
            const finalPlan = prod.plans[pk];

            // 1~53주차에 대해 연도별 데이터 집계
            for (let w = 1; w <= 53; w++) {
                // 플로우 지표: 모든 연도의 수량을 합산 (+=)
                let sumOrder = 0;
                let sumSales = 0;
                let sumShipment = 0;
                let sumProd = 0;
                let sumMo = 0;
                let hasAnyFlow = false;

                // 재고(Stock): 최신 연도의 해당 주차 재고 값 반영 (없으면 직전 연도)
                let latestStock = undefined;

                years.forEach(yr => {
                    const yrPlan = yearlyResolvedPlans[yr]?.[pk] || yearlyResolvedPlans[yr]?.["ALL"] || yearlyResolvedPlans[yr]?.[defaultBaseWeek];
                    if (!yrPlan) return;

                    if (yrPlan.orders[w] !== undefined) { sumOrder += yrPlan.orders[w]; hasAnyFlow = true; }
                    if (yrPlan.sales[w] !== undefined) { sumSales += yrPlan.sales[w]; hasAnyFlow = true; }
                    if (yrPlan.shipments[w] !== undefined) { sumShipment += yrPlan.shipments[w]; hasAnyFlow = true; }
                    if (yrPlan.productions[w] !== undefined) { sumProd += yrPlan.productions[w]; hasAnyFlow = true; }
                    if (yrPlan.moOrders[w] !== undefined) { sumMo += yrPlan.moOrders[w]; hasAnyFlow = true; }

                    if (yrPlan.stocks[w] !== undefined) {
                        latestStock = yrPlan.stocks[w];
                    }
                });

                if (hasAnyFlow || years.length > 0) {
                    finalPlan.orders[w] = sumOrder;
                    finalPlan.sales[w] = sumSales;
                    finalPlan.shipments[w] = sumShipment;
                    finalPlan.productions[w] = sumProd;
                    finalPlan.moOrders[w] = sumMo;
                }
                if (latestStock !== undefined) {
                    finalPlan.stocks[w] = latestStock;
                }
            }
        });

        // 각 연도별로 온전하게 구성된 플랜 데이터를 품목 객체에 보관
        prod.yearlyPlans = yearlyResolvedPlans;
    });

    return Object.values(productMap);
}

/**
 * 백엔드 BSN_MNGMT API 조회 함수
 */
let currentFetchToken = 0;

export async function fetchSupplyPlanFromDB(searchParams = {}) {
    const fetchToken = ++currentFetchToken;
    isDataLoading.set(true);

    // 전달받은 조건과 현재 UI 조건을 병합하여 '실제 적용된 조건'으로 확정
    const currentSearch = get(supplyPlanSearch);
    const mergedSearch = { ...currentSearch, ...searchParams };
    appliedSupplyPlanSearch.set(JSON.parse(JSON.stringify(mergedSearch)));

    try {
        const baseWeek = mergedSearch.baseWeek;
        const compareWeek = mergedSearch.compareWeek;
        const isMultiWeek = mergedSearch.isMultiWeek !== undefined ? mergedSearch.isMultiWeek : false;
        const startDate = mergedSearch.startDate || autoWeekRange.startDate;
        const endDate = mergedSearch.endDate || autoWeekRange.endDate;
        const weekInfo = getWeekRangeInfo(startDate, endDate) || autoWeekRange;
        const startYear = weekInfo.startYear;
        const endYear = weekInfo.endYear;
        const isMonthSum = Boolean(mergedSearch.monthSum);
        const startWeek = weekInfo.startWeek;
        const endWeek = weekInfo.endWeek;
        const itemKeyword = mergedSearch.itemKeyword || mergedSearch.itemCode;
        const productType = mergedSearch.productType;
        const itemGroup = mergedSearch.itemGroup;
        const category = mergedSearch.category;
        const modelIntegration = mergedSearch.modelIntegration;
        const model = mergedSearch.model;

        const selectedItemGroups = Array.isArray(mergedSearch.selectedItemGroups) && mergedSearch.selectedItemGroups.length > 0 ? mergedSearch.selectedItemGroups : null;
        const selectedModels = Array.isArray(mergedSearch.selectedModels) && mergedSearch.selectedModels.length > 0 ? mergedSearch.selectedModels : null;
        const selectedGroupModels = Array.isArray(mergedSearch.selectedGroupModels) && mergedSearch.selectedGroupModels.length > 0 ? mergedSearch.selectedGroupModels : null;

        const baseParams = {
            start_year: startYear ? String(startYear).trim() : null,
            end_year: endYear ? String(endYear).trim() : null,
            plan_week_no: (baseWeek && baseWeek !== "ALL") ? String(baseWeek).trim() : null,
            compare_week_no: (isMultiWeek && compareWeek && compareWeek !== "ALL") ? String(compareWeek).trim() : null,
            item_keyword: itemKeyword ? String(itemKeyword).trim() : null,
            mlsfc_code: (productType && productType !== "ALL") ? String(productType).trim() : null,
            item_group_code: (itemGroup && itemGroup !== "ALL") ? String(itemGroup).trim() : ((category && category !== "ALL") ? String(category).trim() : null),
            mhrls_unity_code: (modelIntegration && modelIntegration !== "ALL") ? String(modelIntegration).trim() : null,
            mhrls_knd_code: (model && model !== "ALL") ? String(model).trim() : null,
            item_group_list: selectedItemGroups && selectedItemGroups.length > 0 ? selectedItemGroups.map(x => String(x).trim()) : null,
            model_list: selectedModels && selectedModels.length > 0 ? selectedModels.map(x => String(x).trim()) : null,
            group_model_list: selectedGroupModels ? selectedGroupModels.map(x => String(x).trim()) : null,
            start_week_no: startWeek ? String(startWeek).trim() : "1",
            end_week_no: endWeek ? String(endWeek).trim() : "53",
        };

        // 전체 데이터를 한 번에 조회하며, 배열 파라미터(item_group_list 등)는 반복 키 형태로 직렬화
        const res = await axios.get("bsnMngmt/list", {
            params: baseParams,
            paramsSerializer: (params) => {
                const searchParams = new URLSearchParams();
                Object.entries(params).forEach(([key, value]) => {
                    if (value === null || value === undefined || value === "") return;
                    if (Array.isArray(value)) {
                        value.forEach(item => {
                            if (item !== null && item !== undefined && item !== "") {
                                searchParams.append(key, item);
                            }
                        });
                    } else {
                        searchParams.append(key, value);
                    }
                });
                return searchParams.toString();
            }
        });
        if (fetchToken !== currentFetchToken) return;

        let list = [];
        if (res && res.data && res.data.code === 200 && res.data.data) {
            if (Array.isArray(res.data.data)) {
                list = res.data.data;
            } else if (res.data.data.list && Array.isArray(res.data.data.list)) {
                list = res.data.data.list;
            }
        }

        if (list.length > 0) {
            const products = transformBsnMngmtRowsToProducts(list, baseWeek, compareWeek);
            rawProductsStore.set(products);
            
            dataStatus.set({
                isFromApi: true,
                message: `DB 실데이터 (${products.length}개 품목)`,
                count: products.length,
                totalItems: products.length
            });
            isDataLoading.set(false);
            return { isFromApi: true, count: products.length, totalItems: products.length };
        } else {
            rawProductsStore.set([]);
            dataStatus.set({
                isFromApi: true,
                message: "해당 조건의 데이터가 없습니다 (0건)",
                count: 0
            });
            isDataLoading.set(false);
            return { isFromApi: true, count: 0 };
        }
    } catch (e) {
        console.warn("공급계획 API 호출 중 오류 발생:", e);
        rawProductsStore.set([]);
        dataStatus.set({
            isFromApi: false,
            message: "API 연결 오류 (" + (e.message || "오류") + ")",
            count: 0
        });
        isDataLoading.set(false);
        return { isFromApi: false, count: 0, error: e };
    }
}

// 해당 항목(key)의 주차/월/총계 값 중 0이 아닌 값이 하나라도 있는지 확인 (0 제외 행 판단용)
function hasNonZeroMetric(metrics, key) {
    if (!metrics) return false;
    const hasNz = bucket => Object.values(bucket || {}).some(d => d && (Number(d[key]) || 0) !== 0);
    return hasNz(metrics.weeksData)
        || hasNz(metrics.monthsData)
        || (metrics.totalData ? (Number(metrics.totalData[key]) || 0) !== 0 : false);
}

// 제품 컬럼 정렬 상태 (기본: 품번 오름차순, 헤더 클릭 시 해당 컬럼 오름 ↔ 내림 전환)
export const DEFAULT_SORT = { key: "itemCode", dir: "asc" };
export const supplyPlanSort = writable({ ...DEFAULT_SORT });

const SORT_COLLATOR = new Intl.Collator("ko", { numeric: true, sensitivity: "base" });

export const filteredSupplyPlan = derived(
    [appliedSupplyPlanSearch, visibleMasterColumns, visibleMetricKeys, rawProductsStore, supplyPlanSort],
    ([$search, $visibleCols, $visibleMetricKeys, $rawProducts, $sort]) => {
        const {
            excludeZero,
            isMultiWeek,
            monthSum,
            startYear,
            endYear,
            baseWeek,
            compareWeek,
            startWeek,
            endWeek,
            itemKeyword,
            productType,
            itemGroup,
            modelIntegration,
            model,
            category,
            itemCode,
            region,
            team,
            customer,
        } = $search;

        const startDate = $search.startDate || autoWeekRange.startDate;
        const endDate = $search.endDate || autoWeekRange.endDate;
        const isMonthSum = Boolean(monthSum);

        // 표시할 항목(수주/판매/차질/출하/생산/재고) 정의 - 설정된 항목만, 비어있으면 전체
        const selectedMetricDefs = METRIC_DEFINITIONS.filter(m => ($visibleMetricKeys || []).includes(m.key));
        const metricDefs = selectedMetricDefs.length > 0 ? selectedMetricDefs : METRIC_DEFINITIONS;

        const currentYearNum = new Date().getFullYear();

        // 날짜 구간 기반 연속된 주차 및 월 목록 생성 (다년도 걸침 100% 지원)
        const { activeWeeks, activeMonths } = generateActiveWeeksAndMonths(startDate, endDate, baseWeek, currentYearNum);

        // 1. 제품 필터링
        const filteredProducts = $rawProducts.filter(product => {
            if (team !== "ALL" && product.team !== team) return false;
            if (region !== "ALL" && product.region !== region) return false;
            
            // 제품유형 필터 (다중 선택 우선, 없을 경우 단일 선택)
            const pTypeCode = String(product.productTypeCode || "").trim();
            const pTypeNm = String(product.productType || "").trim();
            if (Array.isArray($search.selectedProductTypes) && $search.selectedProductTypes.length > 0) {
                const matchPt = $search.selectedProductTypes.some(pt => {
                    const t = String(pt).trim();
                    return pTypeCode === t || pTypeNm === t;
                });
                if (!matchPt) return false;
            } else if (productType && productType !== "ALL") {
                const t = String(productType).trim();
                if (pTypeCode !== t && pTypeNm !== t) return false;
            }

            // 품목그룹 필터 (트리 선택 우선: 전체 선택된 품목그룹 또는 일부 기종만 선택된 "품목그룹|기종" 쌍, 없을 경우 단일 선택)
            const pGrpCode = String(product.itemGroupCode || "").trim();
            const pGrpNm = String(product.itemGroup || "").trim();
            const selTreeGroups = Array.isArray($search.selectedItemGroups) ? $search.selectedItemGroups : [];
            const selTreePairs = Array.isArray($search.selectedGroupModels) ? $search.selectedGroupModels : [];
            if (selTreeGroups.length > 0 || selTreePairs.length > 0) {
                const matchGrp = selTreeGroups.some(g => {
                    const t = String(g).trim();
                    return pGrpCode === t || pGrpNm === t || matchPattern(pGrpNm, t);
                });
                const matchPair = selTreePairs.some(p => {
                    const [pg, pm] = String(p).split("|");
                    const gCode = String(pg || "").trim();
                    const mCode = String(pm || "").trim();
                    if (pGrpCode !== gCode) return false;
                    // 기종 코드가 없는 행은 서버에서 이미 필터링된 결과이므로 통과
                    return !product.modelCode || product.modelCode === mCode || product.model === mCode;
                });
                if (!matchGrp && !matchPair) return false;
            } else {
                const effGroup = (itemGroup && itemGroup !== "ALL") ? itemGroup : ((category && category !== "ALL") ? category : "");
                if (effGroup) {
                    const t = String(effGroup).trim();
                    if (pGrpCode !== t && pGrpNm !== t && !matchPattern(pGrpNm, t)) return false;
                }
            }

            // 통합기종 필터
            if (modelIntegration && modelIntegration !== "ALL") {
                if (product.integratedModelCode !== modelIntegration && product.integratedModel !== modelIntegration && !matchPattern(product.integratedModel, modelIntegration)) return false;
            }

            // 기종 필터 (다중 선택 우선, 없을 경우 단일 선택)
            if (Array.isArray($search.selectedModels) && $search.selectedModels.length > 0) {
                const matchMdl = $search.selectedModels.some(m => product.modelCode === m || product.model === m || matchPattern(product.model, m));
                if (!matchMdl) return false;
            } else if (model && model !== "ALL") {
                if (product.modelCode !== model && product.model !== model && !matchPattern(product.model, model)) return false;
            }

            // 품목조회 (품목코드, 품명 함께 조회: 뭐라도 있으면 조회됨)
            const kw = itemKeyword || itemCode;
            if (kw) {
                const matchCode = matchPattern(product.itemCode, kw);
                const matchName = matchPattern(product.itemName, kw);
                if (!matchCode && !matchName) return false;
            }

            if (customer && !matchPattern(product.customer, customer)) return false;

            return true;
        });

        // 2. 선택된 제품 컬럼($visibleCols) 기반 Dynamic GROUP BY 처리
        const groupByCols = Array.isArray($visibleCols) && $visibleCols.length > 0
            ? $visibleCols
            : ["itemCode"];

        const groupMap = new Map();

        filteredProducts.forEach(product => {
            const keyParts = groupByCols.map(col => String(product[col] ?? "-").trim());
            const groupKey = keyParts.join("___");

            if (!groupMap.has(groupKey)) {
                const groupProduct = {
                    id: groupKey,
                    ...product,
                };
                groupByCols.forEach(col => {
                    groupProduct[col] = product[col] || "-";
                });

                groupMap.set(groupKey, {
                    groupKey,
                    groupProduct,
                    products: [],
                });
            }

            groupMap.get(groupKey).products.push(product);
        });

        // 3. 각 그룹별 PSI 집계 및 metrics 계산
        const items = [];
        const METRIC_KEYS = ["order", "sales", "shortage", "shipment", "production", "stock"];
        const FLOW_KEYS = ["order", "sales", "shortage", "shipment", "production"];

        function createZeroMetrics() {
            const weeksData = {};
            const monthsData = {};
            activeWeeks.forEach(wItem => {
                weeksData[wItem.key] = { order: 0, sales: 0, shortage: 0, shipment: 0, production: 0, stock: 0 };
            });
            activeMonths.forEach(mObj => {
                monthsData[mObj.key] = { order: 0, sales: 0, shortage: 0, shipment: 0, production: 0, stock: 0 };
            });
            const totalData = { order: 0, sales: 0, shortage: 0, shipment: 0, production: 0, stock: 0 };
            return { weeksData, monthsData, totalData };
        }

        const lastMonthKey = (activeMonths && activeMonths.length > 0)
            ? activeMonths[activeMonths.length - 1].key
            : "";

        groupMap.forEach(({ groupProduct, products }) => {
            const groupBase = createZeroMetrics();
            const groupCompare = createZeroMetrics();

            // 그룹 내 모든 품번의 주차별 데이터 합산
            products.forEach(p => {
                const pBase = calculatePSIMetrics(p, baseWeek, activeMonths, activeWeeks);
                const pCompare = calculatePSIMetrics(p, compareWeek, activeMonths, activeWeeks);

                activeWeeks.forEach(wItem => {
                    const k = wItem.key;
                    METRIC_KEYS.forEach(mKey => {
                        groupBase.weeksData[k][mKey] += (pBase.weeksData[k] ? pBase.weeksData[k][mKey] : 0);
                        groupCompare.weeksData[k][mKey] += (pCompare.weeksData[k] ? pCompare.weeksData[k][mKey] : 0);
                    });
                });
            });

            // 그룹의 월별 데이터 집계
            activeMonths.forEach(mObj => {
                const mKey = mObj.key;
                FLOW_KEYS.forEach(k => {
                    let sumBase = 0;
                    let sumCompare = 0;
                    mObj.weeks.forEach(wItem => {
                        sumBase += groupBase.weeksData[wItem.key] ? groupBase.weeksData[wItem.key][k] : 0;
                        sumCompare += groupCompare.weeksData[wItem.key] ? groupCompare.weeksData[wItem.key][k] : 0;
                    });
                    groupBase.monthsData[mKey][k] = sumBase;
                    groupCompare.monthsData[mKey][k] = sumCompare;
                });

                // 재고(Stock): 그달의 마지막 주차의 기말 재고 값
                let lastWItem = (mObj.weeks && mObj.weeks.length > 0) ? mObj.weeks[mObj.weeks.length - 1] : null;
                groupBase.monthsData[mKey].stock = lastWItem && groupBase.weeksData[lastWItem.key] ? groupBase.weeksData[lastWItem.key].stock : 0;
                groupCompare.monthsData[mKey].stock = lastWItem && groupCompare.weeksData[lastWItem.key] ? groupCompare.weeksData[lastWItem.key].stock : 0;
            });

            // 그룹의 총계(Total) 데이터 집계
            FLOW_KEYS.forEach(k => {
                let sumBase = 0;
                let sumCompare = 0;
                activeWeeks.forEach(wItem => {
                    sumBase += groupBase.weeksData[wItem.key] ? groupBase.weeksData[wItem.key][k] : 0;
                    sumCompare += groupCompare.weeksData[wItem.key] ? groupCompare.weeksData[wItem.key][k] : 0;
                });
                groupBase.totalData[k] = sumBase;
                groupCompare.totalData[k] = sumCompare;
            });

            // 총계 재고: 조회 기간 중 가장 마지막 달의 재고
            groupBase.totalData.stock = lastMonthKey && groupBase.monthsData[lastMonthKey] ? groupBase.monthsData[lastMonthKey].stock : 0;
            groupCompare.totalData.stock = lastMonthKey && groupCompare.monthsData[lastMonthKey] ? groupCompare.monthsData[lastMonthKey].stock : 0;

            const diffMetrics = calculateDiffMetrics(groupBase, groupCompare, activeMonths, activeWeeks);

            // 0 제외: 한 항목(행)의 모든 값이 0이면 해당 행만 제거, 남는 행이 없으면 품목 자체를 제외
            let rowMetricDefs = metricDefs;
            if (excludeZero) {
                const checkTargets = isMultiWeek ? [groupBase, groupCompare] : [groupBase];
                rowMetricDefs = metricDefs.filter(def => checkTargets.some(m => hasNonZeroMetric(m, def.key)));
                if (rowMetricDefs.length === 0) {
                    return;
                }
            }

            items.push({
                product: groupProduct,
                baseMetrics: groupBase,
                compareMetrics: groupCompare,
                diffMetrics,
                metricDefs: rowMetricDefs,
            });
        });

        // 3-1. 제품 컬럼 정렬 (선택된 컬럼이 숨겨져 있으면 품번 오름차순으로 대체, 동일 값은 기존 순서 유지)
        const sortKey = groupByCols.includes($sort.key) ? $sort.key : (groupByCols.includes(DEFAULT_SORT.key) ? DEFAULT_SORT.key : null);
        if (sortKey) {
            const sortDir = groupByCols.includes($sort.key) && $sort.dir === "desc" ? -1 : 1;
            items.sort((a, b) => sortDir * SORT_COLLATOR.compare(String(a.product[sortKey] ?? ""), String(b.product[sortKey] ?? "")));
        }

        // 4. 전체 합계(Total) 계산
        const grandBase = createZeroMetrics();
        const grandCompare = createZeroMetrics();

        items.forEach(({ baseMetrics, compareMetrics }) => {
            activeWeeks.forEach(wItem => {
                const k = wItem.key;
                METRIC_KEYS.forEach(mKey => {
                    grandBase.weeksData[k][mKey] += (baseMetrics.weeksData[k] ? baseMetrics.weeksData[k][mKey] : 0);
                    grandCompare.weeksData[k][mKey] += (compareMetrics.weeksData[k] ? compareMetrics.weeksData[k][mKey] : 0);
                });
            });

            activeMonths.forEach(mObj => {
                const k = mObj.key;
                METRIC_KEYS.forEach(mKey => {
                    grandBase.monthsData[k][mKey] += (baseMetrics.monthsData[k] ? baseMetrics.monthsData[k][mKey] : 0);
                    grandCompare.monthsData[k][mKey] += (compareMetrics.monthsData[k] ? compareMetrics.monthsData[k][mKey] : 0);
                });
            });

            METRIC_KEYS.forEach(mKey => {
                grandBase.totalData[mKey] += baseMetrics.totalData[mKey];
                grandCompare.totalData[mKey] += compareMetrics.totalData[mKey];
            });
        });

        // 재고(Stock): 전체 합계에서도 조회 기간 중 제일 마지막 달의 전체합계 재고 값으로 확실하게 동기화
        if (lastMonthKey && grandBase.monthsData[lastMonthKey]) {
            grandBase.totalData.stock = grandBase.monthsData[lastMonthKey].stock;
        }
        if (lastMonthKey && grandCompare.monthsData[lastMonthKey]) {
            grandCompare.totalData.stock = grandCompare.monthsData[lastMonthKey].stock;
        }

        const grandDiff = calculateDiffMetrics(grandBase, grandCompare, activeMonths, activeWeeks);

        // 전체 합계 행도 0 제외 시 값이 있는 항목만 표시
        const grandMetricDefs = excludeZero
            ? metricDefs.filter(def => (isMultiWeek ? [grandBase, grandCompare] : [grandBase]).some(m => hasNonZeroMetric(m, def.key)))
            : metricDefs;

        return {
            items,
            totalItemCount: filteredProducts.length,
            startYear,
            activeWeeks,
            activeMonths,
            grandBase,
            grandCompare,
            grandDiff,
            isMultiWeek,
            baseWeek: baseWeek === "ALL" ? "전체" : baseWeek,
            compareWeek: compareWeek === "ALL" ? "전체" : compareWeek,
            visibleCols: groupByCols,
            grandMetricDefs,
            monthSummaryMode: isMonthSum,
        };
    }
);

// CSV 내보내기 유틸리티 함수
export function exportToCSV(data) {
    const { items, startYear, activeWeeks, activeMonths, grandBase, isMultiWeek, baseWeek, compareWeek, visibleCols, monthSummaryMode } = data;
    
    // 선택된 마스터 컬럼 헤더 매핑
    const activeMasterDefs = (visibleCols || []).map(k => MASTER_COLUMNS_DEF.find(c => c.key === k)).filter(Boolean);
    const headers = activeMasterDefs.map(c => c.label);
    headers.push("계획주차", "항목");

    // 주차 및 월별 헤더 (월 합계 모드에서는 주차/총계 컬럼 제외)
    (activeMonths || []).forEach(mObj => {
        if (!monthSummaryMode) {
            (mObj.weeks || []).forEach(w => {
                const wLabel = typeof w === "object" ? w.label : `${w}주`;
                headers.push(wLabel);
            });
        }
        headers.push(`${mObj.label}`);
    });
    headers.push("계(총계)");

    const rows = [headers.join(",")];

    // 한 계획주차(기준/비교/차이)의 항목별 행을 CSV 행으로 변환
    const pushMetricRows = (product, metrics, weekLabel, defs) => {
        defs.forEach(metric => {
            const row = activeMasterDefs.map(c => `"${product[c.key] || ''}"`);
            row.push(`"${weekLabel}"`, `"${metric.label}"`);

            (activeMonths || []).forEach(mObj => {
                const mKey = mObj.key || String(mObj.month);
                if (!monthSummaryMode) {
                    (mObj.weeks || []).forEach(w => {
                        const wKey = typeof w === "object" ? w.key : String(w);
                        const val = metrics.weeksData[wKey] ? metrics.weeksData[wKey][metric.key] : 0;
                        row.push(val);
                    });
                }
                const mVal = metrics.monthsData[mKey] ? metrics.monthsData[mKey][metric.key] : 0;
                row.push(mVal);
            });

            row.push(metrics.totalData[metric.key] || 0);
            rows.push(row.join(","));
        });
    };

    // 화면과 동일하게: 기준주차 → (복수주차비교 시) 비교주차 → 차이(Diff) 순서로 출력
    items.forEach(({ product, baseMetrics, compareMetrics, diffMetrics, metricDefs }) => {
        const defs = metricDefs || METRIC_DEFINITIONS;
        pushMetricRows(product, baseMetrics, baseWeek, defs);
        if (isMultiWeek) {
            pushMetricRows(product, compareMetrics, compareWeek, defs);
            pushMetricRows(product, diffMetrics, "차이(Diff)", defs);
        }
    });

    const csvContent = "\uFEFF" + rows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    const weekTag = isMultiWeek ? `${baseWeek}_vs_${compareWeek}` : baseWeek;
    link.setAttribute("download", `공급계획_PSI_${weekTag}_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}
